import React, { useEffect, useRef, useState } from 'react'
import { deleteDealImage, getDealImages, setImageAsAvatar, uploadDealImage, type DealImage } from './crmApi'
import { ConfirmDialog, IconTrash, IconUpload, crmToast } from './crmUi'
import { Button } from '../shared/Button'

/**
 * IMAGES section on a lead (Lee, 7 Sep 2026). Pictures uploaded here; the one
 * marked as avatar shows everywhere the company logo would (board, task
 * dashboard, queue, planner…) — UNTIL the account is linked to WeQuote, at
 * which point the real logo pulls through and always wins. The pictures stay
 * stored here either way.
 */

const fmtSize = (n: number | null) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`)

export const DealImages: React.FC<{ dealId: string; hasWequoteLogo: boolean; onChanged?: () => void }> = ({ dealId, hasWequoteLogo, onChanged }) => {
  const [images, setImages] = useState<DealImage[] | null>(null)
  const [confirm, setConfirm] = useState<DealImage | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = () => getDealImages(dealId).then(setImages).catch(() => setImages([]))
  useEffect(() => { setImages(null); load() }, [dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length) return
    setBusy(true)
    try {
      for (const f of Array.from(files)) {
        if (!/^image\//.test(f.type)) { crmToast(`${f.name} is not an image`, 'error'); continue }
        if (f.size > 5 * 1024 * 1024) { crmToast(`${f.name} is over 5 MB`, 'error'); continue }
        await uploadDealImage(dealId, f)
      }
      crmToast('Uploaded', 'success')
      load(); onChanged?.()
    } catch { crmToast('Upload failed', 'error') } finally { setBusy(false) }
  }

  const makeAvatar = (img: DealImage) =>
    setImageAsAvatar(img.id).then(() => { crmToast('Set as the lead avatar', 'success'); load(); onChanged?.() }).catch(() => crmToast('Could not set the avatar', 'error'))

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-[12px] text-txt-secondary max-w-lg">
          {hasWequoteLogo
            ? 'This account is linked to WeQuote, so its real logo is the avatar. Pictures uploaded here stay stored, and take over again if the logo ever stops coming through.'
            : 'No WeQuote logo yet — the image marked as avatar is shown everywhere for this lead until the account gets linked and the logo pulls through.'}
        </p>
        <Button size="sm" loading={busy} onClick={() => fileRef.current?.click()} leftIcon={<IconUpload className="w-3.5 h-3.5" />}>Upload images</Button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = '' }} />
      </div>

      {!images ? (
        <p className="text-[12px] text-txt-tertiary">Loading…</p>
      ) : images.length === 0 ? (
        <p className="text-[12px] text-txt-tertiary">No images on this lead yet — upload the first above. The first upload becomes the avatar automatically.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.id} className={`rounded-xl border overflow-hidden bg-surface ${img.isAvatar ? 'border-brand-500 ring-1 ring-brand-500/30' : 'border-border'}`}>
              <a href={img.url} target="_blank" rel="noreferrer" title="Open full size">
                <img src={img.url} alt={img.name || 'Lead image'} className="w-full h-28 object-cover bg-surface-secondary" loading="lazy" />
              </a>
              <div className="px-2.5 py-2">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[11px] text-txt-primary truncate" title={img.name || undefined}>{img.name || 'Image'}</span>
                  <span className="text-[9px] text-txt-tertiary shrink-0">{fmtSize(img.size)}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  {img.isAvatar ? (
                    <span className="text-[10px] font-semibold text-brand-600" title={hasWequoteLogo ? 'Would be the avatar — the linked WeQuote logo currently wins' : 'Shown everywhere as this lead’s avatar'}>
                      ✓ Avatar{hasWequoteLogo ? ' (logo wins)' : ''}
                    </span>
                  ) : (
                    <button onClick={() => makeAvatar(img)} className="text-[10px] text-txt-tertiary hover:text-brand-600 hover:underline">Make avatar</button>
                  )}
                  <button onClick={() => setConfirm(img)} className="inline-flex text-txt-tertiary hover:text-red-600" title="Delete image">
                    <IconTrash className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete this image?"
          message={<>“{confirm.name || 'Image'}” will be removed{confirm.isAvatar ? ' — the newest remaining image becomes the avatar' : ''}.</>}
          confirmLabel="Delete"
          onConfirm={async () => { await deleteDealImage(confirm.id); load(); onChanged?.() }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
